import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "./AuthContext";
import axiosInstance from "services/httpInterceptor";
import { useLocation } from "react-router-dom";

// Create context for analytics data
export const AnalyticsContext = createContext();

// Custom hook to use the analytics context
export const useAnalytics = () => {

  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }
  return context;
};

// Provider component that wraps the app
export const AnalyticsProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const initialPresetKey = "last_quarter"; // Default preset value

  const [selectedCompetitor, setSelectedCompetitor] = useState(null);
  const [selectedPresetKey, setSelectedPresetKey] = useState(initialPresetKey);
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState(null);
  // State for the selected social media platform (defaults to null)
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  // State for the R&D Landscape condition filter (defaults to "ALL")
  const [rdConditionFilter, setRdConditionFilter] = useState("ALL");

  // State for the R&D Landscape competitor selection (defaults to "market" — the all-competitors view)
  const [rdCompetitorId, setRdCompetitorId] = useState("market");

  // State for the Product Showdown selectors
  // anchorFamily: which Terumo product the rep sells ("FRED", "SOFIA Plus")
  // competitorFamily: which competitor product to compare against
  const [showdownAnchor, setShowdownAnchor] = useState(null);
  const [showdownCompetitor, setShowdownCompetitor] = useState(null);
  // Anchor and competitor option lists — populated by the Product Showdown page
  const [showdownAnchors, setShowdownAnchors] = useState([]);
  const [showdownCompetitors, setShowdownCompetitors] = useState([]);

  // Set company ID from user data when authenticated
  useEffect(() => {
    if (isAuthenticated && user?.companyId) {
      setCompanyId(user.companyId);
    }
  }, [isAuthenticated, user]);

  const handlePresetChange = useCallback((presetKey) => {
    setSelectedPresetKey(presetKey);
  }, []);

  // Fetch competitors when company ID is available
  useEffect(() => {
    const fetchCompetitors = async () => {
      if (!companyId) return;

      setLoading(true);
      try {
        const response = await axiosInstance.get(`/v1/companies/${companyId}/competitors`);
        const fetchedCompetitors = response.data || [];
        setCompetitors(fetchedCompetitors);

        if (!selectedCompetitor && fetchedCompetitors.length > 0) {
          const defaultCompetitor = fetchedCompetitors[0];
          if (defaultCompetitor) {
            setSelectedCompetitor(defaultCompetitor.id);
          }
        }
        // Reset the R&D condition filter whenever the competitor list reloads
        setRdConditionFilter("ALL");
      } catch (error) {
        console.error("Error fetching competitors:", error);
        setCompetitors([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCompetitors();
  }, [companyId]);

  // When entering a /v2/ route, default the competitor selector to "all_competitors".
  // Uses location.pathname as the trigger so it fires on every navigation to /v2/.
  // The ref prevents re-applying if the user manually switches competitor within the session.
  const v2DefaultAppliedRef = useRef(false);
  useEffect(() => {
    const isV2Route = location.pathname.includes("/v2/");
    if (!isV2Route) {
      // Left v2 — reset so next v2 visit gets the default again
      v2DefaultAppliedRef.current = false;
      return;
    }
    if (!v2DefaultAppliedRef.current) {
      // Apply default regardless of whether competitors have loaded yet
      setSelectedCompetitor("all_competitors");
      v2DefaultAppliedRef.current = true;
    }
  // location.pathname is the only trigger needed — competitors loading is irrelevant
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Runs on every render/route change.
  // Ensures page-specific virtual options ("market_overview", "market_trends", "all_competitors")
  // never leak to pages that don't support them, preventing 404/500 errors in other components.
  const effectiveSelectedCompetitor = useMemo(() => {
    const path = location.pathname;

    if (selectedCompetitor === "market_overview" && !path.includes("competitor-reports")) {
      return competitors.length > 0 ? competitors[0].id : null;
    }

    if (selectedCompetitor === "market_trends" && !path.includes("expansion-trends")) {
      return competitors.length > 0 ? competitors[0].id : null;
    }

    if (selectedCompetitor === "all_competitors" && !path.includes("/v2/")) {
      return competitors.length > 0 ? competitors[0].id : null;
    }

    return selectedCompetitor;
  }, [selectedCompetitor, location.pathname, competitors]);

  useEffect(() => {
    const isVirtual =
      selectedCompetitor === "market_overview" ||
      selectedCompetitor === "market_trends";
    if (isVirtual && effectiveSelectedCompetitor !== selectedCompetitor) {
      setSelectedCompetitor(effectiveSelectedCompetitor);
    }
  }, [effectiveSelectedCompetitor, selectedCompetitor]);

  const value = useMemo(() => ({
    selectedCompetitor: effectiveSelectedCompetitor,
    setSelectedCompetitor,
    selectedPresetKey,
    handlePresetChange,
    competitors,
    loading,
    companyId,
    setCompanyId,
    companyName: user?.companyName || "Your Company",
    selectedPlatform,
    setSelectedPlatform,
    rdConditionFilter,
    setRdConditionFilter,
    rdCompetitorId,
    setRdCompetitorId,
    showdownAnchor,
    setShowdownAnchor,
    showdownCompetitor,
    setShowdownCompetitor,
    showdownAnchors,
    setShowdownAnchors,
    showdownCompetitors,
    setShowdownCompetitors,
  }), [
    effectiveSelectedCompetitor,
    selectedPresetKey,
    competitors,
    loading,
    companyId,
    user?.companyName,
    handlePresetChange,
    selectedPlatform,
    rdConditionFilter,
    rdCompetitorId,
    showdownAnchor,
    showdownCompetitor,
    showdownAnchors,
    showdownCompetitors,
  ]);

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};